import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchEntities } from '../../api/entities';
import { useGlobalSearch } from '../../api/intelligence';

const SHORTCUTS = [
  { label: 'Dashboard', path: '/dashboard', keys: 'D' },
  { label: 'Entites', path: '/entities', keys: 'E' },
  { label: 'Graphe', path: '/graph', keys: 'G' },
  { label: 'Alertes', path: '/alerts', keys: 'A' },
  { label: 'Investigation', path: '/investigation', keys: 'I' },
  { label: 'Workflows', path: '/workflows', keys: 'W' },
  { label: 'Anomalies', path: '/anomaly', keys: 'N' },
  { label: 'Carte Geo', path: '/geomap', keys: 'M' },
  { label: 'Rapports', path: '/reports', keys: 'R' },
  { label: 'Audit', path: '/audit', keys: 'U' },
  { label: 'Parametres', path: '/settings', keys: 'S' },
];

const TYPE_ICONS: Record<string, string> = {
  person: 'bg-blue-500/20 text-blue-400',
  organization: 'bg-purple-500/20 text-purple-400',
  location: 'bg-green-500/20 text-green-400',
  ip_address: 'bg-red-500/20 text-red-400',
  domain: 'bg-orange-500/20 text-orange-400',
  email: 'bg-cyan-500/20 text-cyan-400',
};

const MODULE_STYLES: Record<string, string> = {
  darkweb: 'bg-red-500/20 text-red-400',
  alerts: 'bg-yellow-500/20 text-yellow-400',
  osint: 'bg-emerald-500/20 text-emerald-400',
  relationships: 'bg-indigo-500/20 text-indigo-400',
  entities: 'bg-blue-500/20 text-blue-400',
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { data: entityResults } = useSearchEntities(open && query.length >= 2 ? query : '');
  const { data: globalResults } = useGlobalSearch(open && query.length >= 2 ? query : '');

  // Build combined results list
  const results: Array<{ type: 'page' | 'entity' | 'module'; label: string; sublabel?: string; path: string; icon?: string }> = [];

  if (!query) {
    // Show navigation shortcuts
    SHORTCUTS.forEach((s) => {
      results.push({ type: 'page', label: s.label, path: s.path, icon: s.keys });
    });
  } else {
    // Filter shortcuts
    const filtered = SHORTCUTS.filter((s) => s.label.toLowerCase().includes(query.toLowerCase()));
    filtered.forEach((s) => {
      results.push({ type: 'page', label: s.label, path: s.path, icon: s.keys });
    });

    // Entity results
    if (entityResults) {
      entityResults.slice(0, 6).forEach((e) => {
        results.push({
          type: 'entity',
          label: e.name,
          sublabel: e.type.replace('_', ' '),
          path: `/entities/${e.id}`,
          icon: e.type,
        });
      });
    }

    // Global module results
    if (globalResults?.results) {
      globalResults.results
        .filter((r) => r.module !== 'entities')
        .slice(0, 5)
        .forEach((r) => {
          results.push({
            type: 'module',
            label: r.title,
            sublabel: r.module,
            path: r.url || `/entities/${r.id}`,
            icon: r.module,
          });
        });
    }
  }

  // Keyboard shortcut to open
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Clamp selected index
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelect = useCallback(
    (path: string) => {
      setOpen(false);
      setQuery('');
      navigate(path);
    },
    [navigate],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex].path);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-surface-secondary border border-border-subtle rounded-2xl shadow-2xl shadow-black/40 overflow-hidden animate-fade-in">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 border-b border-border-subtle">
          <svg className="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder="Rechercher entites, pages, modules..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 py-4 bg-transparent text-sm text-gray-200 placeholder-gray-500 focus:outline-none"
          />
          <kbd className="px-1.5 py-0.5 text-[10px] font-mono text-gray-500 bg-surface-primary rounded border border-border-subtle">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto py-2">
          {results.length === 0 && query.length >= 2 && (
            <div className="px-4 py-8 text-center text-sm text-gray-500">Aucun resultat pour &quot;{query}&quot;</div>
          )}

          {!query && (
            <div className="px-4 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
              Navigation rapide
            </div>
          )}

          {query && results.some((r) => r.type === 'page') && (
            <div className="px-4 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Pages</div>
          )}

          {results
            .filter((r) => r.type === 'page')
            .map((result, idx) => {
              const globalIdx = results.indexOf(result);
              return (
                <button
                  key={`page-${result.path}`}
                  onClick={() => handleSelect(result.path)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    selectedIndex === globalIdx ? 'bg-cyber-cyan/10 text-cyber-cyan' : 'text-gray-300 hover:bg-surface-tertiary'
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg bg-surface-tertiary flex items-center justify-center text-xs font-bold text-gray-400">
                    {result.icon}
                  </div>
                  <span className="text-sm font-medium">{result.label}</span>
                  <span className="ml-auto text-[10px] text-gray-600">Aller a la page</span>
                </button>
              );
            })}

          {results.some((r) => r.type === 'entity') && (
            <div className="px-4 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider mt-1 border-t border-border-subtle pt-2">
              Entites
            </div>
          )}

          {results
            .filter((r) => r.type === 'entity')
            .map((result) => {
              const globalIdx = results.indexOf(result);
              return (
                <button
                  key={`entity-${result.path}`}
                  onClick={() => handleSelect(result.path)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    selectedIndex === globalIdx ? 'bg-cyber-cyan/10 text-cyber-cyan' : 'text-gray-300 hover:bg-surface-tertiary'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                      TYPE_ICONS[result.icon || ''] || 'bg-gray-500/20 text-gray-400'
                    }`}
                  >
                    {result.label.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{result.label}</p>
                    <p className="text-xs text-gray-500 capitalize">{result.sublabel}</p>
                  </div>
                </button>
              );
            })}

          {results.some((r) => r.type === 'module') && (
            <div className="px-4 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider mt-1 border-t border-border-subtle pt-2">
              Modules
            </div>
          )}

          {results
            .filter((r) => r.type === 'module')
            .map((result) => {
              const globalIdx = results.indexOf(result);
              return (
                <button
                  key={`mod-${result.path}-${result.label}`}
                  onClick={() => handleSelect(result.path)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    selectedIndex === globalIdx ? 'bg-cyber-cyan/10 text-cyber-cyan' : 'text-gray-300 hover:bg-surface-tertiary'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold ${
                      MODULE_STYLES[result.icon || ''] || 'bg-gray-500/20 text-gray-400'
                    }`}
                  >
                    {(result.sublabel || '').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{result.label}</p>
                    <p className="text-xs text-gray-500 uppercase">{result.sublabel}</p>
                  </div>
                </button>
              );
            })}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 px-4 py-2.5 border-t border-border-subtle text-[10px] text-gray-600">
          <span>
            <kbd className="px-1 py-0.5 bg-surface-tertiary rounded border border-border-subtle mr-0.5">↑</kbd>
            <kbd className="px-1 py-0.5 bg-surface-tertiary rounded border border-border-subtle mr-1">↓</kbd>
            naviguer
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-surface-tertiary rounded border border-border-subtle mr-1">↵</kbd>
            ouvrir
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-surface-tertiary rounded border border-border-subtle mr-1">esc</kbd>
            fermer
          </span>
        </div>
      </div>
    </div>
  );
}
